import React, { useEffect, useRef, useState } from "react";
import { connectStripeAccount, getStripeConnectStatus } from "../../../../database/stripe/stripeApi";
import { StripeConnectStatus } from "../../../../types/Stripe";

const labels: Record<string, string> = {
  valid: "Authenticates", invalid_key: "Invalid or revoked key — reconnect", missing: "Not configured",
  permission_denied: "Required read permission is missing", unavailable: "Could not verify — retry",
  verified: "Verified", unverified: "Not verified", needs_repair: "Needs repair — reconnect",
  enabled: "Payments enabled", charges_disabled: "Stripe has not enabled charges", account_mismatch: "Account does not match",
  invalid: "Inactive or mismatched prices",
};
export default function StripePaymentsPanel({ communityId, canEdit = true }: { communityId: string; canEdit?: boolean }) {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);
  useEffect(() => {
    setSecret(""); setStatus(null); setError("");
    const id = ++request.current;
    if (communityId && canEdit) {
      setBusy(true);
      getStripeConnectStatus(communityId).then(s => { if (request.current === id) setStatus(s); })
        .catch(() => { if (request.current === id) setError("Could not verify Stripe. Check your community access and retry."); })
        .finally(() => { if (request.current === id) setBusy(false); });
    }
    return () => { request.current++; };
  }, [communityId, canEdit]);
  async function run(connect: boolean) {
    const id = ++request.current;
    const key = secret.trim();
    setSecret(""); // Clear on every attempt, including failures; never persist locally.
    setBusy(true); setError("");
    try {
      const result = connect ? await connectStripeAccount({ communityId, secretKey: key }) : await getStripeConnectStatus(communityId);
      if (id === request.current) setStatus(result);
    } catch {
      if (id === request.current) setError("Could not verify or save the connection. Check the restricted key permissions and use the same Stripe account and mode when rotating. Your stored key is unchanged if connection setup failed.");
    } finally { if (id === request.current) setBusy(false); }
  }
  if (!canEdit) return <p>An owner or editor of this community can manage Payments.</p>;
  const label = (value?: string) => labels[value || "unverified"] || value;
  return <section aria-label="Payments and Stripe" style={{ background: "var(--color-surface, white)", border: "1px solid var(--color-border, #ddd)", borderRadius: 8, padding: 24, maxWidth: 900 }}>
    <h2>Payments / Stripe</h2>
    <p>Connect your own Stripe account for web payments. Enter keys here, never in WhatsApp, product links, or support messages.</p>
    {error && <p role="alert" style={{ color: "#a32b24" }}>{error}</p>}
    <div aria-live="polite" aria-busy={busy}>
      {busy && <p>Checking Stripe…</p>}
      {status && <>
        <dl>
          <dt>Stored credential</dt><dd>{status.keyStored ? "Key stored on the server" : "No key stored"}</dd>
          <dt>Authentication</dt><dd>{label(status.credentialStatus)}</dd>
          <dt>Stripe account</dt><dd>{label(status.accountStatus)} · {status.livemode ? "Live mode" : status.livemode === false ? "Test mode" : "Mode unknown"}</dd>
          <dt>Webhook configuration</dt><dd>{label(status.webhookStatus)}</dd>
        </dl>
        <h3>One-time digital products</h3>
        <p>{status.oneTimePayments?.configurationReady ? "Account and webhook configuration verified." : "Account or webhook configuration still needs attention."} Each product also needs a valid price and active, protected fulfillment.</p>
        <p>No subscription prices, manually created Stripe products, or payment links are required. Checkout builds the one-time price automatically.</p>
        {!!status.digitalProducts?.length && <ul>{status.digitalProducts.map(product => <li key={product.productId}>
          {product.name}: {product.configurationReady ? "configuration ready (payment not tested)" : product.blockers.map(reason => reason.replace(/_/g, " ")).join(", ")}
          {product.delivery === "external_link" ? " · external delivery link" : ""}
        </li>)}</ul>}
        <h3>Membership subscriptions</h3>
        <p>Prices: {label(status.subscriptions?.pricesStatus)}. Membership checkout: {status.subscriptions?.enabled ? "enabled" : "disabled"}.</p>
        <p>Read-only checks do not prove Checkout write permission or webhook delivery. Nothing has been purchased, published, or enabled by this test.</p>
        {status.checkedAt && <small>Last checked: {new Date(status.checkedAt).toLocaleString()}</small>}
      </>}
    </div>
    <div style={{ marginTop: 20 }}>
      <label htmlFor="stripe-restricted-key">Restricted Stripe key</label>
      <input id="stripe-restricted-key" className="sentry-mask" data-private="true" type="password" autoComplete="new-password" spellCheck={false}
        value={secret} onChange={e => setSecret(e.target.value)} disabled={busy} placeholder="rk_test_… or rk_live_…"
        style={{ display: "block", padding: 10, width: "100%", maxWidth: 520, margin: "8px 0" }} />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button type="button" disabled={busy || !/^rk_(test|live)_[A-Za-z0-9]+$/.test(secret.trim())} onClick={() => run(true)}>{status?.keyStored ? "Reconnect / rotate key" : "Connect Stripe"}</button>
        <button type="button" disabled={busy || !communityId} onClick={() => run(false)}>Test stored connection</button>
      </div>
      <p>Reconnect retains the same account and test/live mode. We never display a saved key. Connecting may register or repair the fulfillment webhook, but does not activate products or membership checkout.</p>
    </div>
    <details>
      <summary>Required restricted-key permissions</summary>
      <p>In Stripe Dashboard → Developers → API keys, create a restricted key for Tribed. Give only the resources needed for the features you use.</p>
      <ul>
        <li>Accounts: Read, for authentication and same-account rotation checks.</li>
        <li>Checkout Sessions: Write (includes Read), for checkout and connection checks.</li>
        <li>Webhook Endpoints: Write, to register, inspect, and repair fulfillment events.</li>
        <li>Products and Prices: Write, for inline checkout prices and membership price provisioning.</li>
        <li>Customers: Write; Subscriptions: Read; Invoices: Read, for membership checkout, lifecycle handling, and revenue reporting.</li>
        <li>Customer portal (Billing Portal): Write, if members use the billing portal. Charges and Payment Intents: Read, for payment/refund lookup.</li>
      </ul>
      <p>These are the permissions mapped to this integration's API calls. A read-only connection test cannot inspect every write permission. Stripe may require permissions for related or expanded resources. No balance transfers, payouts, or refunds write access is requested.</p>
      <p><a href="https://docs.stripe.com/keys" target="_blank" rel="noreferrer">Stripe API key instructions</a> · <a href="https://docs.stripe.com/keys-best-practices" target="_blank" rel="noreferrer">Key security</a> · <a href="https://docs.stripe.com/api/checkout/sessions/create" target="_blank" rel="noreferrer">Checkout API</a></p>
    </details>
  </section>;
}
